import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowDown, PencilLine } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HsId, PageHeader, Row, Section, Spinner, StatusBadge } from "@/components/kit";
import ApprovalBar from "@/components/ApprovalBar";
import ActivityList from "@/components/ActivityList";
import Documents from "@/components/Documents";
import { Approvals, Revisions } from "@/pages/TxnDetail";
import { ago, fdate, inr } from "@/lib/format";

export default function SettlementDetail() {
  const { id } = useParams();
  const { person } = useAuth();
  const [s, setS] = useState(null);
  const load = () => api.get(`/settlements/${id}`).then((r) => setS(r.data));
  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return <Spinner />;
  const nameOf = (pid) => (pid === s.from_person_id ? s.from_name : pid === s.to_person_id ? s.to_name : pid?.slice(0, 6));
  const last = s.approvals?.[0];
  return (
    <div className="pb-6">
      <PageHeader title="Settlement" subtitle={s.asset_name ? <Link className="text-[#C8553D] font-semibold" to={`/holding/${s.holding_id}`}>{s.asset_name}</Link> : "Group-level settlement"}
        right={<StatusBadge status={s.status} testid="settlement-status" />} />
      <div className="space-y-4">
        {s.can_act && <ApprovalBar endpoint={`/settlements/${id}/action`} onDone={load} />}
        {s.status === "edit_requested" && <div className="hd-card bg-blue-50 text-sm text-blue-800">{last?.by_name} requested an edit: “{last?.comment}”</div>}
        {s.status === "rejected" && <div className="hd-card bg-red-50 text-sm text-red-800">Rejected by {last?.by_name}: “{last?.comment}”</div>}
        {s.status === "pending" && !s.can_act && <div className="hd-card bg-amber-50 text-sm text-amber-800" data-testid="awaiting-banner">Waiting for the other Karta to confirm.</div>}
        <div className="hd-card">
          <div className="text-3xl font-bold tabular-nums" data-testid="settlement-amount">{inr(s.amount)}</div>
          <div className="mt-4 rounded-2xl bg-[#F5F0E9] p-3">
            <div className="flex items-center justify-between"><div><div className="text-xs text-[#718096]">From</div><div className="font-semibold">{s.from_name}</div></div><HsId value={s.from_hs_id} /></div>
            <ArrowDown size={16} className="my-1.5 text-[#C8553D]" />
            <div className="flex items-center justify-between"><div><div className="text-xs text-[#718096]">To</div><div className="font-semibold">{s.to_name}</div></div><HsId value={s.to_hs_id} /></div>
          </div>
          <div className="mt-3">
            <Row label="Date">{fdate(s.date)}</Row>
            <Row label="Mode">{s.payment_mode}</Row>
            {s.reference && <Row label="Reference">{s.reference}</Row>}
            {s.notes && <Row label="Notes">{s.notes}</Row>}
            <Row label="Recorded by">{s.created_by_name} · {ago(s.created_at)}</Row>
            {s.confirmed_by_name && <Row label="Confirmed by">{s.confirmed_by_name}{s.requires_confirmation ? "" : " (same side, auto)"}</Row>}
          </div>
          <div className="text-xs text-[#718096] mt-2">Settlements reduce balances. They never change asset ownership.</div>
          {s.can_edit && s.submitted_by === person.id && (
            <Link to={`/add/settlement?edit=${s.id}`} className="hd-btn-outline w-full mt-3 h-11 text-sm" data-testid="settlement-edit-button"><PencilLine size={16} /> Edit</Link>
          )}
        </div>
        <Section title="Documents" className="mt-0"><Documents entityType="settlement" entityId={s.id} /></Section>
        <Approvals approvals={s.approvals} />
        <Revisions revisions={s.revisions} nameOf={nameOf} />
        <Section title="Activity"><ActivityList items={s.activity} testid="settlement-activity" /></Section>
      </div>
    </div>
  );
}
